import React, { useEffect, useRef, useState } from 'react';
import api from '../../lib/axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Eye, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import EmailPreviewConfirm from '../../components/email/EmailPreviewConfirm';
import { payerNameLabel } from '@/lib/utils';

function plainToPreviewHtml(text) {
  const esc = (s) => String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  const paras = esc(text).split(/\n\n+/).map((p) => `<p>${p.replace(/\n/g, '<br/>')}</p>`).join('');
  return `<html><body style="font-family:sans-serif;color:#0f172a">${paras}</body></html>`;
}

export function SinglePreviewSendDialog({ guardian }) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [mailPreview, setMailPreview] = useState(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setMailPreview(null);
    api.get('/correspondence/templates').then((r) => {
      setTemplates(r.data);
      const sys = r.data.find((t) => t.system_key === 'billing_preview');
      if (sys) {
        setTemplateId(sys.id);
        setSubject(sys.subject || '');
        setBody(sys.body || '');
      }
    }).catch(() => {});
    api.get('/correspondence/placeholders').then((r) => setPlaceholders(r.data)).catch(() => {});
  }, [open]);

  const applyTemplate = (id) => {
    setTemplateId(id);
    const t = templates.find((x) => x.id === id);
    if (t) {
      if (t.subject) setSubject(t.subject);
      if (t.body) setBody(t.body);
    }
  };

  const insertPlaceholder = (key) => {
    const el = bodyRef.current;
    if (!el) { setBody((b) => b + key); return; }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    setBody(body.slice(0, start) + key + body.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = start + key.length;
    });
  };

  const openPreview = () => {
    if (!body.trim() && !subject.trim()) {
      toast.error('Bitte Betreff oder Text eingeben');
      return;
    }
    setMailPreview({
      subject: subject || 'Beitragsaufstellung',
      html: plainToPreviewHtml(body || '(kein Begleittext — nur PDF-Anhang)'),
    });
  };

  const send = async (routing = {}) => {
    setSending(true);
    try {
      const r = await api.post(`/guardians/${guardian.id}/billing-preview-email`, {
        subject, body, template_id: templateId || undefined, preview_confirmed: true,
        ...routing,
      });
      toast.success(r.data.queued_for_approval
        ? `E-Mail an ${r.data.recipient} eingereiht (wartet auf Freigabe)`
        : `Beitragsaufstellung an ${r.data.recipient} gesendet`);
      setMailPreview(null);
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'E-Mail-Versand fehlgeschlagen');
    } finally { setSending(false); }
  };

  return (
    <>
      <button
        onClick={() => {
          if (!guardian.email) { toast.error('Keine E-Mail-Adresse hinterlegt'); return; }
          setOpen(true);
        }}
        className="flex items-center gap-1 text-xs text-brand-red hover:text-brand-red border border-brand-red/20 hover:bg-brand-red/5 rounded px-2 py-1 transition-colors"
        title={guardian.email ? `Als PDF an ${guardian.email} senden` : 'Keine E-Mail-Adresse hinterlegt'}
        data-testid="billing-preview-email-btn"
      >
        <Eye className="h-3.5 w-3.5" /> Per E-Mail senden
      </button>
      <Dialog open={open && !mailPreview} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="single-preview-send-dialog">
          <DialogHeader>
            <DialogTitle>Beitragsaufstellung senden</DialogTitle>
            <DialogDescription>
              PDF-Anhang wird automatisch erzeugt. Empfänger: <span className="font-medium text-slate-700">{guardian.email}</span>
              {' '}— Versand nur nach HTML-Voransicht.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-500">Textvorlage (Korrespondenz)</label>
              <Select value={templateId} onValueChange={applyTemplate}>
                <SelectTrigger className="mt-1" data-testid="single-send-template-select">
                  <SelectValue placeholder="Vorlage wählen (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.is_favorite ? '★ ' : ''}{t.module} · {t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Betreff</label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1" data-testid="single-send-subject" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Text</label>
              <Textarea ref={bodyRef} value={body} onChange={(e) => setBody(e.target.value)} rows={8} className="mt-1 font-mono text-xs" data-testid="single-send-body" />
              <div className="flex flex-wrap gap-1 mt-2">
                {placeholders.map((p) => (
                  <button
                    key={p.key}
                    onClick={() => insertPlaceholder(p.key)}
                    title={p.label}
                    className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 hover:bg-brand-red/10 hover:text-brand-red font-mono transition-colors"
                    data-testid={`single-send-placeholder-${p.key.replace(/[<>]/g, '')}`}
                  >
                    {p.key}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="single-send-cancel-btn">Abbrechen</Button>
              <Button onClick={openPreview} data-testid="single-send-confirm-btn">
                <Eye className="h-4 w-4 mr-1.5" />
                Voransicht
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <EmailPreviewConfirm
        open={!!mailPreview}
        onClose={() => setMailPreview(null)}
        onConfirm={send}
        confirming={sending}
        title="Beitragsaufstellung — Voransicht"
        description="Bitte Begleittext prüfen. PDF-Anhang wird mitgesendet."
        to={guardian.email}
        toLabel={payerNameLabel(guardian.last_name, guardian.first_name)}
        subject={mailPreview?.subject}
        html={mailPreview?.html}
        attachmentNote="Anhang: Beitragsaufstellung.pdf"
        testId="billing-preview-email-confirm"
      />
    </>
  );
}
